import type {
  DsdEditorMappingReview,
  DsdEditorMappingReviewTarget,
  DsdEditorNoteReferenceCheck,
  DsdEditorValueMatch,
  DsdEditorValueOccurrence,
} from "@/apis/dsd-editor/route";
import { NOTE_REFERENCE_STATUS_META } from "@/components/dsd-table-view";
import { getOccurrenceAxisLabel } from "../note-reference-review-panel-ui";
import {
  NOTE_REFERENCE_CELL_FILL,
  type NOTE_REFERENCE_SOFT_CLASS,
} from "@/utils/dsd-editor/note-reference-colors";
import type {
  DsdValueLinkVerdict,
  PlainDsdNoteReferenceStatus,
} from "@/types/plain-dsd-table";
import type {
  DsdEditorParsedItem,
  DsdEditorSelectedCellSummary,
} from "@/utils/dsd-editor/document-items";
import {
  buildNoteReviewCards,
  getOccurrenceIds,
  getReviewTargetChunkId,
  isNoteOccurrence,
  occurrenceMatchesId,
  sameMappingReviewPair,
} from "@/utils/dsd-editor/note-reference-review";
import { NOTE_REFERENCE_CANDIDATE_TONE } from "@/utils/dsd-editor/note-reference";
import { DSD_REVIEW_COPY, DSD_REVIEW_STATUS_LABELS } from "../copy";

export type DsdReviewMatchItem = {
  key: string;
  /** 주석 청크의 후보인가 (본문 안 후보와 태그로 구분) */
  isNote: boolean;
  /** 필수 확인 = 참조 주석, 추가 확인 = 다른 주석·참조 주석 없음·본문 안 후보 */
  group: "required" | "additional";
  /**
   * 본문 주석번호와의 관계. referenced = 본문이 그 주석을 참조, other = 본문에 주석번호는 있는데 다른 주석,
   * unreferenced = 본문에 주석번호가 없음. body = 본문(재무제표) 쪽 후보.
   * unreferenced 는 내부 구분용(그룹·noEvidenceBanner 선택) — 화면 태그 문구와 칩 판정은 other 와 같다
   */
  tag: "referenced" | "other" | "unreferenced" | "body";
  title: string;
  axisLabel: string;
  value: string;
  /** 참조 주석·본문 안은 일치 항목(초록), 다른 주석·참조 주석 없음은 불일치 항목(주황) */
  matchLabel: string;
  matchTone: "positive" | "caution";
  /** 근거 창·테두리 색 — 그 셀과의 연결 판정(valueLinkVerdictOf: 확인/미확인/주기 없음). 없으면 선택 셀 배너 톤 */
  tone: DsdReviewBannerTone;
  /** 검토 쌍. 백엔드가 "검토 필요" 로 준 상대에만 있다 */
  target: DsdEditorMappingReviewTarget | null;
  /** 저장된 판단. target 이 있고 이게 없으면 미검토 */
  review: DsdEditorMappingReview | null;
  occurrence: DsdEditorValueOccurrence;
  /** 값 후보이거나 텍스트 연결이 있어 근거 창에 보여줄 표가 있는가. 청크 쌍(둘 다 아님)은 근거 없음 안내 */
  hasEvidence: boolean;
  /** 연결을 만든 하드룰 id(베타에서만 표시). 일반 판정이면 null */
  hardRule: string | null;
};

/** 검토 쌍이 있는 카드 — 검토 모달은 이것만 받는다 (target 없는 카드로 열리는 것을 컴파일 타임에 막는다) */
export type DsdReviewPendingItem = DsdReviewMatchItem & {
  target: DsdEditorMappingReviewTarget;
};
/** 셀에 채움색이 없는 상태 — 코치마크 대상 제외와 "연결된 주석이 없습니다" 판정이 같은 기준을 쓴다 */
const isColorlessStatus = (status: PlainDsdNoteReferenceStatus) =>
  NOTE_REFERENCE_STATUS_META[status].bgClassName === "";

/**
 * 진입 코치마크를 붙일 셀인가 — 색이 칠해진 상태 셀이거나 후보 셀. 클릭은 되지만 색이 없는 셀(unlinked)을
 * 잡으면 안내를 따라 눌렀는데 "연결된 주석이 없습니다" 만 보게 된다
 */
export const isDsdReviewHintTargetCell = (
  cellId: string,
  statusByCellId: ReadonlyMap<string, PlainDsdNoteReferenceStatus>,
  candidateCellIds: ReadonlySet<string>,
) => {
  const status = statusByCellId.get(cellId);
  return (
    (status !== undefined && !isColorlessStatus(status)) ||
    candidateCellIds.has(cellId)
  );
};

/** 검토 필요(백엔드가 쌍을 준) 카드인가 */
export const needsReview = (
  item: DsdReviewMatchItem,
): item is DsdReviewPendingItem => item.target !== null;
/** 검토 필요인데 아직 판단이 없는 카드만 — "검토 필요 항목만 보기" */
export const isPendingReview = (
  item: DsdReviewMatchItem,
): item is DsdReviewPendingItem => needsReview(item) && item.review === null;
/**
 * 패널에 보이고 근거 창이 ←/→ 로 도는 후보 — "검토 필요 항목만 보기" 면 미검토만.
 * 검토 쌍이 하나도 없는 셀에는 필터를 걸지 않는다 — 걸면 카드가 전부 숨고 스위치도(검토 쌍이 있을 때만 보임) 없어 못 끈다.
 * 패널 카드의 순번과 근거 창의 1/N 이 같은 목록을 써야 하므로 규칙은 여기 하나
 */
export const getDsdReviewEvidenceItems = (
  items: DsdReviewMatchItem[],
  pendingOnly: boolean,
) =>
  pendingOnly && items.some(needsReview)
    ? items.filter(isPendingReview)
    : items;

export type DsdReviewBannerTone = keyof typeof NOTE_REFERENCE_SOFT_CLASS;

export type DsdReviewSelectedCellView = {
  cellId: string;
  sectionName: string;
  axisLabel: string;
  value: string;
  /** 상태 배너. 상태가 없거나 색이 없는 셀은 null */
  banner: { text: string; tone: DsdReviewBannerTone } | null;
  /** 셀에 색이 없는 상태(unlinked — 0·"-" 셀 포함) — 배너 대신 "연결된 주석이 없습니다" */
  noLinkedNote: boolean;
  items: DsdReviewMatchItem[];
};

// 셀 채움색과 같은 계열로 배너 톤을 정한다 (초록/노랑/빨강, 그 외 중립)
const toneOfStatus = (
  status: PlainDsdNoteReferenceStatus,
): DsdReviewBannerTone => {
  const fill = NOTE_REFERENCE_STATUS_META[status].bgClassName;
  if (fill === NOTE_REFERENCE_CELL_FILL.positive) return "positive";
  if (fill === NOTE_REFERENCE_CELL_FILL.caution) return "caution";
  if (fill === NOTE_REFERENCE_CELL_FILL.negative) return "negative";
  // 셀에 색이 없는 상태는 배너 대신 안내 문구라(noLinkedNote) 배너로는 쓰이지 않는다. 카드 기본 톤으로만 — 경고(노랑)
  return "caution";
};

/**
 * 상태 → 배너. Figma 문구가 있는 상태만 여기서 정하고, 나머지는 셀 툴팁과 같은 문구(DSD_REVIEW_STATUS_LABELS)·톤.
 * unconfirmed(근거 하나뿐)는 주기 표기 여부로 갈린다 — 주기가 있으면 "계정명이 달라 불확실", 없으면 "주석번호 표기가 없음"
 */
const bannerOf = (
  status: PlainDsdNoteReferenceStatus | null,
  hasReferencedLink: boolean,
): DsdReviewSelectedCellView["banner"] => {
  if (!status) return null;
  const copy = DSD_REVIEW_COPY.panel.banner;
  switch (status) {
    case "confirmed":
      return { text: copy.matched, tone: "positive" };
    case "reference_missing":
      return { text: copy.referenceMissing, tone: "negative" };
    case "unconfirmed":
      return {
        text: hasReferencedLink
          ? copy.valueOnlyUncertain
          : copy.unreferencedPartial,
        tone: "caution",
      };
    default:
      return {
        text: DSD_REVIEW_STATUS_LABELS[status],
        tone: toneOfStatus(status),
      };
  }
};

const VERDICT_TONE: Record<DsdValueLinkVerdict, DsdReviewBannerTone> = {
  confirmed: "positive",
  unconfirmed: "caution",
  reference_missing: "negative",
};

/**
 * 카드 칩. 값 후보(fromValue)면 금액이, 텍스트 연결(fromText)이면 계정명이 맞은 것.
 * 검토 필요 여부와 무관하게 태그로 정한다 — 참조 주석 카드는 검토 쌍이 있어도(주황 테두리·⚠) 일치 항목을
 * 초록으로 보여준다. 카드 테두리는 "확인하라", 칩은 "무엇이 맞았나" 로 뜻이 다르다 (디자인 스펙)
 * 다른 주석·참조 주석 없음은 불일치 항목을 주황으로, 그 외(참조 주석·본문 안)는 일치 항목을 초록으로
 */
const matchLabelOf = (
  card: { fromValue: boolean; fromText: boolean; value: string },
  tag: DsdReviewMatchItem["tag"],
): Pick<DsdReviewMatchItem, "matchLabel" | "matchTone"> => {
  const copy = DSD_REVIEW_COPY.panel.matchLabel;
  const noAmount = !card.value.trim() || card.value.trim() === "-";
  if (tag === "other" || tag === "unreferenced") {
    if (card.fromValue && card.fromText)
      return { matchLabel: copy.both, matchTone: "positive" };
    if (card.fromValue)
      return { matchLabel: copy.textMismatch, matchTone: "caution" };
    if (card.fromText)
      return noAmount
        ? { matchLabel: copy.noAmount, matchTone: "caution" }
        : { matchLabel: copy.amountMismatch, matchTone: "caution" };
    return { matchLabel: copy.bothMismatch, matchTone: "caution" };
  }
  if (card.fromValue && card.fromText)
    return { matchLabel: copy.both, matchTone: "positive" };
  if (card.fromValue) return { matchLabel: copy.value, matchTone: "positive" };
  if (card.fromText) return { matchLabel: copy.text, matchTone: "positive" };
  return { matchLabel: copy.bothMismatch, matchTone: "caution" };
};

/**
 * 선택 셀의 패널 내용: 셀 요약 + 상태 + 값 후보 카드.
 * 패널은 항상 match 의 본문(source) 셀을 설명한다 — 주석 쪽 후보 셀을 직접 눌러도 요약·상태·후보는
 * 그 후보가 속한 본문 셀 기준이다. match 가 없는 셀(상태만 있는 셀)은 자기 자신.
 */
export const buildDsdReviewSelectedCellView = ({
  selectedCellId,
  cellSummaryById,
  activeValueMatch,
  statusByCellId,
  noteReferenceChecks,
  colorStatusByCellId,
}: {
  selectedCellId: string | null;
  cellSummaryById: ReadonlyMap<string, DsdEditorSelectedCellSummary>;
  activeValueMatch: DsdEditorValueMatch | null;
  statusByCellId: ReadonlyMap<string, PlainDsdNoteReferenceStatus>;
  noteReferenceChecks: DsdEditorNoteReferenceCheck[];
  /**
   * 색 전용 상태(검토 결과로 파생, cell-verdict.ts). 있으면 배너 **톤**만 여기서 가져오고
   * 문구는 statusByCellId(백엔드 상태)로 정한다 — 파생 값은 초록/노랑/빨강 셋뿐이라 사실 설명이 안 된다
   */
  colorStatusByCellId?: ReadonlyMap<string, PlainDsdNoteReferenceStatus>;
}): DsdReviewSelectedCellView | null => {
  if (!selectedCellId) return null;
  const source = activeValueMatch?.source ?? null;
  const sourceCellId =
    (source &&
      getOccurrenceIds(source).find((id) => cellSummaryById.has(id))) ??
    selectedCellId;
  const summary = cellSummaryById.get(sourceCellId);
  if (!summary) return null;
  const sourceChecks = noteReferenceChecks.filter((check) =>
    occurrenceMatchesId(check.source, sourceCellId),
  );
  const valueLinks = sourceChecks.flatMap((check) => check.valueLinks);
  // 본문 셀이 참조한 주석 청크 — 여기 속하면 참조 주석, 아니면 다른 주석. 본문에 주석번호가 아예 없으면 참조 주석 없음
  const bodyHasNoteNumbers = sourceChecks.some(
    (check) => check.noteReferences.length > 0,
  );
  const referencedChunkIds = new Set(
    sourceChecks
      .flatMap((check) => check.noteReferences)
      .map((reference) => reference.chunkId)
      .filter((chunkId): chunkId is string => !!chunkId),
  );
  // 검토 쌍 — 여러 check 가 같은 쌍을 가질 수 있어 중복 제거
  const pairs: DsdEditorMappingReviewTarget[] = [];
  for (const check of sourceChecks) {
    for (const target of check.reviewTargets) {
      if (!pairs.some((seen) => sameMappingReviewPair(seen, target)))
        pairs.push(target);
    }
  }

  const status = statusByCellId.get(sourceCellId) ?? null;
  const statusBanner = bannerOf(
    status,
    valueLinks.some((link) => link.referenced),
  );
  // 파생 색이 백엔드 상태와 다를 때(카드·판단이 색을 바꿨을 때)만 배너 톤을 덮는다.
  // 같으면 상태 자체의 톤 — 파생 맵은 후보 셀 색 표(unlinked → 초록)로 톤을 읽어 "제한됨" 배너가 초록이 됐었다
  const colorStatus = colorStatusByCellId?.get(sourceCellId);
  const banner =
    statusBanner && colorStatus && colorStatus !== status
      ? { ...statusBanner, tone: NOTE_REFERENCE_CANDIDATE_TONE[colorStatus] }
      : statusBanner;
  const fallbackTone = banner ? banner.tone : "positive";

  // 값 후보와 텍스트 연결 셀을 셀 단위로 합친 카드. 체크는 reviewTargets 에서만
  const { cards, rows } = buildNoteReviewCards({
    candidates: (activeValueMatch?.candidates ?? []).map(
      (candidate) => candidate.occurrence,
    ),
    checks: sourceChecks,
    pairs,
  });
  const toItem = (
    card: {
      occurrence: DsdEditorValueOccurrence;
      fromValue: boolean;
      fromText: boolean;
      verdict: DsdValueLinkVerdict | null;
      hardRule: string | null;
      target: DsdEditorMappingReviewTarget | null;
    },
    index: number,
  ): DsdReviewMatchItem => {
    const isNote = isNoteOccurrence(card.occurrence);
    const tag: DsdReviewMatchItem["tag"] = !isNote
      ? "body"
      : !bodyHasNoteNumbers
        ? "unreferenced"
        : referencedChunkIds.has(card.occurrence.chunkId)
          ? "referenced"
          : "other";
    // 본문(재무제표) 안 후보는 일치 케이스로 본다 — 검토 쌍이 와도 검토 대상으로 띄우지 않는다
    // (회색 테두리·⚠·원형 없음, 근거 창을 띄워도 카드를 칠하지 않음, 근거 창 강조는 초록). 주석 후보와 섞여 헷갈려서
    const target = tag === "body" ? null : card.target;
    return {
      key: `${card.occurrence.id}:${index}`,
      isNote,
      // 필수 = 본문이 주기로 가리킨 주석(참조 주석)만. 본문 안 후보는 일치 케이스라 추가 확인으로
      group: tag === "referenced" ? "required" : "additional",
      tag,
      title: card.occurrence.chunkTitle || card.occurrence.chunkId,
      axisLabel: getOccurrenceAxisLabel(card.occurrence),
      value: card.occurrence.value,
      ...matchLabelOf({ ...card, value: card.occurrence.value }, tag),
      // 근거 창의 행·셀 강조도 이 톤을 쓴다. 연결 판정이 없는 주석 후보는 검토 쌍이 있으면 노랑 — 셀 상태(초록)로 되돌리면
      // "검토 필요인데 초록 테두리" 가 된다
      tone:
        tag === "body"
          ? "positive"
          : card.verdict
            ? VERDICT_TONE[card.verdict]
            : target
              ? "caution"
              : fallbackTone,
      target,
      review: target?.review ?? null,
      occurrence: card.occurrence,
      hasEvidence: card.fromValue || card.fromText,
      hardRule: card.hardRule,
    };
  };

  // 링크 없이 온 쌍의 target 은 청크 id 가 아니라 좌표일 수 있다(문단 후보 "note-1:sc0_pg0_p5") — 청크 id 는 getReviewTargetChunkId.
  // 제목은 본문의 주석번호 표기(noteReferences)나 연결(valueLinks)에서 찾고, 없으면 청크 id
  const chunkTitleOf = (chunkId: string) =>
    sourceChecks
      .flatMap((check) => check.noteReferences)
      .find((reference) => reference.chunkId === chunkId)?.chunkTitle ??
    // 연결의 제목은 빈 문자열일 수 있다 — 없음으로 본다
    (valueLinks.find((link) => link.noteChunkId === chunkId)?.noteTitle ||
      undefined);
  const items = [
    ...cards.map(toItem),
    // 셀 카드가 없는 주석 청크와의 쌍 — 표시할 셀이 없어 값 없이 청크 제목만.
    // 주기에만 적힌 주석 행(verdict 있음)은 쌍이 없어도 보인다 — 셀이 노랑인 이유가 패널에 있어야 한다
    ...rows
      .filter((row) => row.target !== null || row.verdict !== null)
      .map((row, index) =>
        toItem(
          {
            occurrence: {
              id: row.chunkId,
              nodeId: row.chunkId,
              chunkId: getReviewTargetChunkId(row.chunkId),
              chunkTitle:
                row.title ?? chunkTitleOf(getReviewTargetChunkId(row.chunkId)),
              rowLabelPath: [],
              columnLabelPath: [],
              value: "",
              blockType: "table",
              valueType: "cell",
            } as DsdEditorValueOccurrence,
            fromValue: false,
            fromText: false,
            verdict: row.verdict,
            hardRule: null,
            target: row.target,
          },
          cards.length + index,
        ),
      ),
  ];
  // 필수 확인 → 추가 확인 순. 패널 섹션과 근거 창의 1/N 번호가 같은 순서를 쓴다
  const GROUP_ORDER = { required: 0, additional: 1 } as const;
  items.sort((a, b) => GROUP_ORDER[a.group] - GROUP_ORDER[b.group]);
  // unconfirmed 의 Figma 문장("금액이 일치하는 주석이 있지만, 계정명이 달라…")은 값만 맞은 경우를 말한다.
  // 참조 주석 중 값도 계정명도 못 찾은 것(청크 쌍)이 있어 unconfirmed 가 된 셀에는 어긋나므로 그 문장으로 바꾼다
  const noteUnverified =
    status === "unconfirmed" &&
    items.some((item) => item.tag === "referenced" && !item.hasEvidence);

  // 셀에 보이는 색(파생 색이 있으면 그것) 기준 — 흰 셀이면 배너 대신 안내 문구. 카드 기본 톤(fallbackTone)은 그대로 둔다
  const shownStatus = colorStatus ?? status;
  const noLinkedNote = shownStatus !== null && isColorlessStatus(shownStatus);

  return {
    cellId: selectedCellId,
    sectionName: summary.sectionName,
    axisLabel: source ? getOccurrenceAxisLabel(source) : "",
    value: summary.value,
    banner: noLinkedNote
      ? null
      : banner && noteUnverified
        ? { ...banner, text: DSD_REVIEW_COPY.panel.noEvidenceBanner.referenced }
        : banner,
    noLinkedNote,
    items,
  };
};

/** 값 매칭에 등장하는 청크(본문 source·주석 후보). 문단 속 값은 셀이 아니라 청크로만 잡힌다 */
export const getDsdReviewMatchChunkIds = (
  valueMatches: DsdEditorValueMatch[],
): ReadonlySet<string> => {
  const ids = new Set<string>();
  for (const match of valueMatches) {
    ids.add(match.source.chunkId);
    for (const candidate of match.candidates)
      ids.add(candidate.occurrence.chunkId);
  }
  return ids;
};

/**
 * 목차 점 배지를 붙일 섹션 — 정보 있는 셀이 하나라도 있거나(본문 상태 셀·주석 후보 셀),
 * 값 매칭이 가리키는 청크인 곳(문단 속 값 후보는 셀이 없어 청크로 판정)
 */
export const getDsdReviewFlaggedSections = (
  items: DsdEditorParsedItem[],
  hasInfo: (cellId: string) => boolean,
  matchChunkIds: ReadonlySet<string>,
): ReadonlySet<string> => {
  const sections = new Set<string>();
  for (const item of items) {
    if (sections.has(item.sectionName)) continue;
    const found =
      matchChunkIds.has(item.chunkId) ||
      (item.kind === "table" &&
        item.table.rows.some((row) =>
          row.cells.some((cell) => hasInfo(cell.cellId)),
        ));
    if (found) sections.add(item.sectionName);
  }
  return sections;
};
